var __defProp = Object.defineProperty;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _stackOrder, _isClosing, _deferredOpen, _previouslyFocused, _modalQuery, _ThemeDrawer_instances, onRestore_fn, _onBackdropClick, hasOpenNestedDialog_fn, getOpenNestedDialog_fn, _onModalBreakpointChange, _onKeyDown, bringToFront_fn, removeEventListeners_fn, focusInitialElement_fn, bringNestedDialogToFront_fn, closeDialog_fn, isTopmost_fn;
import { Component } from "@theme/component";
import { trapFocus, removeTrapFocus } from "@theme/focus";
import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from "@theme/utilities";
const MODAL_BREAKPOINT = 990;
const _ThemeDrawer = class _ThemeDrawer extends Component {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ThemeDrawer_instances);
    __publicField(this, "requiredRefs", ["panel"]);
    /** @type {boolean} */
    __privateAdd(this, _isClosing, false);
    /** When true, the drawer will reopen after the current close animation finishes. */
    /** @type {{ enter?: 'inline-start' } | false} */
    __privateAdd(this, _deferredOpen, false);
    /** Element to restore focus to when the drawer closes. */
    /** @type {HTMLElement | null} */
    __privateAdd(this, _previouslyFocused, null);
    /** @type {MediaQueryList} */
    __privateAdd(this, _modalQuery, window.matchMedia("(max-width: ".concat(MODAL_BREAKPOINT - 1, "px)")));
    /**
     * Closes the drawer when the user clicks the backdrop (outside the dialog
     * content area). Only meaningful in modal mode — in sidebar mode the
     * dialog has no backdrop so this handler is inert.
     *
     * @param {MouseEvent} event - The mouse event.
     */
    __privateAdd(this, _onBackdropClick, (event) => {
      const { panel } = this.refs;
      if (isClickedOutside(event, panel)) {
        if (__privateMethod(this, _ThemeDrawer_instances, hasOpenNestedDialog_fn).call(this)) return;
        this.close();
      }
    });
    /**
     * Switches the dialog between modal and non-modal when the viewport
     * crosses the modal breakpoint while the drawer is open.
     */
    __privateAdd(this, _onModalBreakpointChange, () => {
      if (!this.isOpen) return;
      const { panel } = this.refs;
      const nestedDialog = __privateMethod(this, _ThemeDrawer_instances, getOpenNestedDialog_fn).call(this);
      const nestedActiveElement = (nestedDialog == null ? void 0 : nestedDialog.contains(document.activeElement)) && document.activeElement instanceof HTMLElement ? document.activeElement : null;
      panel.close();
      removeTrapFocus();
      if (__privateGet(this, _modalQuery).matches) {
        lockScroll(panel);
        panel.showModal();
      } else {
        unlockScroll(panel);
        panel.show();
        trapFocus(panel);
      }
      if (nestedDialog == null ? void 0 : nestedDialog.open) {
        __privateMethod(this, _ThemeDrawer_instances, bringNestedDialogToFront_fn).call(this, nestedDialog, nestedActiveElement);
      } else {
        __privateMethod(this, _ThemeDrawer_instances, focusInitialElement_fn).call(this);
      }
      document.addEventListener("keydown", __privateGet(this, _onKeyDown));
      panel.addEventListener("click", __privateGet(this, _onBackdropClick));
    });
    /**
     * Closes the drawer when the user presses Escape.
     * preventDefault() suppresses the native dialog cancel event in modal mode.
     *
     * @param {KeyboardEvent} event - The keyboard event.
     */
    __privateAdd(this, _onKeyDown, (event) => {
      if (event.key !== "Escape") return;
      if (event.isComposing || event.defaultPrevented) return;
      if (!this.isOpen) return;
      if (!__privateMethod(this, _ThemeDrawer_instances, isTopmost_fn).call(this)) return;
      event.preventDefault();
      this.close();
    });
  }
  /**
   * @returns {boolean} Whether the drawer is currently open.
   */
  get isOpen() {
    return this.hasAttribute("open");
  }
  connectedCallback() {
    super.connectedCallback();
    __privateGet(this, _modalQuery).addEventListener("change", __privateGet(this, _onModalBreakpointChange));
    const restoredOrder = Number(this.style.getPropertyValue("--drawer-stack-order") || 0);
    if (restoredOrder > __privateGet(_ThemeDrawer, _stackOrder)) {
      __privateSet(_ThemeDrawer, _stackOrder, restoredOrder);
    }
    if (this.isOpen) {
      __privateMethod(this, _ThemeDrawer_instances, onRestore_fn).call(this);
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    unlockScroll(this.refs.panel);
    __privateGet(this, _modalQuery).removeEventListener("change", __privateGet(this, _onModalBreakpointChange));
    __privateMethod(this, _ThemeDrawer_instances, removeEventListeners_fn).call(this);
    removeTrapFocus();
    __privateSet(this, _deferredOpen, false);
  }
  /**
   * Shows the drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction. `'inline-start'` slides left-to-right (used when the chat docks).
   */
  open({ enter } = {}) {
    if (this.isOpen) {
      __privateMethod(this, _ThemeDrawer_instances, bringToFront_fn).call(this, { enter });
      return;
    }
    if (__privateGet(this, _isClosing)) {
      __privateSet(this, _deferredOpen, { enter });
      return;
    }
    const { panel } = this.refs;
    __privateSet(_ThemeDrawer, _stackOrder, __privateGet(_ThemeDrawer, _stackOrder) + 1);
    this.style.setProperty("--drawer-stack-order", String(__privateGet(_ThemeDrawer, _stackOrder)));
    const openClass = enter === "inline-start" ? "theme-drawer__dialog--opening-inline-start" : "theme-drawer__dialog--opening";
    panel.classList.add(openClass);
    __privateSet(
      this,
      _previouslyFocused,
      /** @type {HTMLElement | null} */
      document.activeElement
    );
    if (__privateGet(this, _modalQuery).matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      panel.show();
      trapFocus(panel);
    }
    __privateMethod(this, _ThemeDrawer_instances, focusInitialElement_fn).call(this);
    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });
    this.setAttribute("open", "");
    this.dispatchEvent(new DrawerOpenEvent());
    document.addEventListener("keydown", __privateGet(this, _onKeyDown));
    panel.addEventListener("click", __privateGet(this, _onBackdropClick));
  }
  /**
   * Closes the drawer.
   */
  async close() {
    if (__privateGet(this, _isClosing)) {
      __privateSet(this, _deferredOpen, false);
      return;
    }
    await __privateMethod(this, _ThemeDrawer_instances, closeDialog_fn).call(this);
  }
  /**
   * Toggles the drawer. If the drawer is open but not the topmost in the
   * stack, it is brought to front instead of closed.
   */
  toggle() {
    if (this.isOpen && __privateMethod(this, _ThemeDrawer_instances, isTopmost_fn).call(this)) {
      return this.close();
    }
    return this.open();
  }
};
_stackOrder = new WeakMap();
_isClosing = new WeakMap();
_deferredOpen = new WeakMap();
_previouslyFocused = new WeakMap();
_modalQuery = new WeakMap();
_ThemeDrawer_instances = new WeakSet();
/**
 * Restore path: the inline script in theme-drawer.liquid set [open] on
 * <theme-drawer> + <dialog> before this module loaded. The dialog is
 * already visible — we just wire close listeners. We deliberately skip
 * trapFocus and focus moves: the user is loading a fresh page and
 * expects focus on main content, not inside a drawer left over from
 * the previous session.
 */
onRestore_fn = function() {
  const { panel } = this.refs;
  if (__privateGet(this, _modalQuery).matches) {
    lockScroll(panel);
  }
  document.addEventListener("keydown", __privateGet(this, _onKeyDown));
  panel.addEventListener("click", __privateGet(this, _onBackdropClick));
};
_onBackdropClick = new WeakMap();
/**
 * @returns {boolean} Whether the drawer panel contains an open dialog other than itself.
 */
hasOpenNestedDialog_fn = function() {
  return __privateMethod(this, _ThemeDrawer_instances, getOpenNestedDialog_fn).call(this) !== null;
};
/**
 * @returns {HTMLDialogElement | null} The open dialog nested inside the drawer panel, if any.
 */
getOpenNestedDialog_fn = function() {
  return (
    /** @type {HTMLDialogElement | null} */
    this.refs.panel.querySelector("dialog[open]")
  );
};
_onModalBreakpointChange = new WeakMap();
_onKeyDown = new WeakMap();
/**
 * Bumps this drawer's z-index to the top of the stack and replays
 * the slide-in animation. Called when open() is invoked on an
 * already-open drawer.
 *
 * @param {object} [options]
 * @param {'inline-start'} [options.enter] - Slide direction.
 */
bringToFront_fn = function({ enter } = {}) {
  const { panel } = this.refs;
  __privateSet(_ThemeDrawer, _stackOrder, __privateGet(_ThemeDrawer, _stackOrder) + 1);
  this.style.setProperty("--drawer-stack-order", String(__privateGet(_ThemeDrawer, _stackOrder)));
  if (__privateGet(this, _modalQuery).matches && panel.open) {
    lockScroll(panel);
    panel.close();
    panel.showModal();
  }
  const openClass = enter === "inline-start" ? "theme-drawer__dialog--opening-inline-start" : "theme-drawer__dialog--opening";
  panel.classList.add(openClass);
  onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });
  this.dispatchEvent(new DrawerOpenEvent());
};
removeEventListeners_fn = function() {
  const { panel } = this.refs;
  document.removeEventListener("keydown", __privateGet(this, _onKeyDown));
  panel.removeEventListener("click", __privateGet(this, _onBackdropClick));
};
/**
 * Moves focus to the close button inside the drawer panel.
 * Every theme-drawer renders the close button via theme-drawer-header.
 * If a future drawer omits it, that drawer is responsible for its own focus management.
 */
focusInitialElement_fn = function() {
  const closeButton = (
    /** @type {HTMLElement | null} */
    this.refs.panel.querySelector(".theme-drawer__close-button")
  );
  closeButton == null ? void 0 : closeButton.focus();
};
/**
 * Reopens a nested modal after the drawer panel changes mode so it remains
 * above the drawer in the browser top layer.
 *
 * @param {HTMLDialogElement} dialog - The nested dialog to restack.
 * @param {HTMLElement | null} focusTarget - Element to refocus inside the nested dialog.
 */
bringNestedDialogToFront_fn = function(dialog, focusTarget) {
  dialog.close();
  dialog.showModal();
  if (focusTarget && document.contains(focusTarget) && dialog.contains(focusTarget)) {
    focusTarget.focus();
  }
};
closeDialog_fn = async function() {
  var _a;
  if (!this.isOpen) return;
  if (__privateGet(this, _isClosing)) return;
  __privateSet(this, _isClosing, true);
  __privateMethod(this, _ThemeDrawer_instances, removeEventListeners_fn).call(this);
  removeTrapFocus();
  const { panel } = this.refs;
  this.removeAttribute("open");
  this.dispatchEvent(new DrawerCloseEvent());
  unlockScroll(panel);
  if (panel.open) {
    panel.classList.remove("theme-drawer__dialog--opening", "theme-drawer__dialog--opening-inline-start");
    panel.classList.add("theme-drawer__dialog--closing");
    await onAnimationEnd(panel, void 0, { subtree: false });
    panel.classList.remove("theme-drawer__dialog--closing");
  }
  panel.close();
  this.style.removeProperty("--drawer-stack-order");
  if (!document.querySelector("theme-drawer[open]")) {
    __privateSet(_ThemeDrawer, _stackOrder, 0);
  }
  const trigger = __privateGet(this, _previouslyFocused);
  __privateSet(this, _previouslyFocused, null);
  if (trigger && document.contains(trigger)) {
    trigger.focus();
  } else {
    (_a = document.querySelector('[aria-controls="'.concat(this.id, '"]'))) == null ? void 0 : _a.focus();
  }
  __privateSet(this, _isClosing, false);
  if (__privateGet(this, _deferredOpen)) {
    const options = __privateGet(this, _deferredOpen);
    __privateSet(this, _deferredOpen, false);
    this.open(options);
  }
};
/**
 * @returns {boolean} Whether this drawer has the highest stack order among all open drawers.
 */
isTopmost_fn = function() {
  const myOrder = Number(this.style.getPropertyValue("--drawer-stack-order") || 0);
  for (const other of document.querySelectorAll("theme-drawer[open]")) {
    if (other === this) continue;
    const otherOrder = Number(
      /** @type {HTMLElement} */
      other.style.getPropertyValue("--drawer-stack-order") || 0
    );
    if (otherOrder > myOrder) return false;
  }
  return true;
};
/** Monotonically increasing counter for z-index stacking of multiple open drawers. */
__privateAdd(_ThemeDrawer, _stackOrder, 0);
let ThemeDrawer = _ThemeDrawer;
if (!customElements.get("theme-drawer")) {
  customElements.define("theme-drawer", ThemeDrawer);
}
const _DrawerOpenEvent = class _DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(_DrawerOpenEvent.eventName, { bubbles: true });
  }
};
__publicField(_DrawerOpenEvent, "eventName", "theme-drawer:open");
let DrawerOpenEvent = _DrawerOpenEvent;
const _DrawerCloseEvent = class _DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(_DrawerCloseEvent.eventName, { bubbles: true });
  }
};
__publicField(_DrawerCloseEvent, "eventName", "theme-drawer:close");
let DrawerCloseEvent = _DrawerCloseEvent;
export {
  DrawerCloseEvent,
  DrawerOpenEvent,
  ThemeDrawer
};
